import EzController from 'controllers/ez-base-controller';
import Cookies from 'js-cookie';
import {
  isHomepage,
  isSearchPage,
  isShowPage,
  getFormattedPageValue,
  featureEnabled,
  thirtyMinutesFromNow,
} from 'lib/helpers';
import {
  canSeePartnerPopup,
  openPopup,
  seenRecently,
  subId1Suffix,
} from 'lib/partner-popup-helpers';
import { sendTrackingEvent } from 'components/analytics/conversions/tracking';
import { PARTNER_POPUP_TRIGGERED } from 'components/analytics/conversions/constants/actions';

const PARTNER_POPUP_PAGINATION_PAGE_TRIGGER = 2;

export default class PartnerPopupController extends EzController {
  static targets = ['currentPage']

  static values = {
    terms: String,
    page: Number,
    totalPages: Number,
    source: String,
  }

  connect() {
    this.impactRadiusUrl = this.element.getAttribute('data-impact-radius-url');
    this.impactRadiusSearchPopupUrl = this.element.getAttribute('data-impact-radius-search-popup-url');
  }

  maybePagination(event) {
    this.mode = 'pagination';

    if (!this.shouldShowPopup()) return;

    const page = this.getNavigationPage(event.target);

    if (featureEnabled('istock_alt_popup_triggers')) {
      const paginationCount = Cookies.get('partner_popup_pagination') || 0;
      const newPaginationCount = parseInt(paginationCount, 10) + 1;

      Cookies.set('partner_popup_pagination', newPaginationCount, { expires: thirtyMinutesFromNow() });

      if (newPaginationCount < PARTNER_POPUP_PAGINATION_PAGE_TRIGGER) return;
    }

    if (this.hasSearchValues) {
      sendTrackingEvent({
        data: {
          action: PARTNER_POPUP_TRIGGERED,
          label: `Terms-${this.termsValue}`,
          value: page,
          partner_popup_origin: this.sourceValue,
        },
      });
    }

    openPopup(this.partnerUrl());
  }

  getNavigationPage(target) {
    if (!target || !this.hasCurrentPageTarget) return this.pageValue;

    if (target === this.currentPageTarget) {
      const page = parseInt(getFormattedPageValue(this.currentPageTarget), 10);

      return page > this.totalPagesValue ? this.totalPagesValue : page;
    }

    return this.pageValue;
  }

  maybeSearch(event = null) {
    if (featureEnabled('istock_alt_popup_triggers')) return;
    if (isHomepage() || !this.shouldShowPopup()) return;
    if (!this.isLucky()) return;

    this.searchForm = event?.currentTarget || null;

    this.mode = 'search';

    openPopup(this.partnerUrl());
  }

  shouldShowPopup() {
    if (featureEnabled('istock_alt_popup_triggers')) return canSeePartnerPopup() && !seenRecently();

    return !seenRecently();
  }

  isLucky() {
    // 3. The lucky number is 3.
    return this.randomNumber() === 3;
  }

  randomNumber() {
    // return a random number between 1 and 5, inclusively
    return Math.floor(Math.random() * 5) + 1;
  }

  get searchTerm() {
    const searchForm = this.searchForm || document.querySelector("[data-controller~='search-form']");

    if (!searchForm) {
      return null;
    }

    if (this.mode === 'pagination') {
      return searchForm.getAttribute('data-search-term').replace(/-/g, ' ');
    }

    const searchInput = searchForm.querySelector('input[name="search"]') || searchForm.querySelector('input[name="search_fullwidth"]');

    if (searchInput) {
      return searchInput.value;
    }

    return null;
  }

  partnerUrl() {
    switch (this.mode) {
      case 'search':
        return this.makePopupUrl(encodeURIComponent(this.subId1()));
      case 'pagination':
        return this.makePopupUrl('pagination');
      default:
        return null;
    }
  }

  makePopupUrl(subId1) {
    const params = {
      u: encodeURIComponent(this.destinationUrl),
      sharedid: window.location.hostname,
      subId1: `${subId1}${subId1Suffix()}`,
      subId2: this.subId2(),
      subId3: this.subId3(),
      adtype: this.adType,
      adplacement: 'POP',
    };

    let impactUrl = '';
    switch (this.mode) {
      case 'search':
        impactUrl = this.impactRadiusSearchPopupUrl;
        break;
      case 'pagination':
        impactUrl = this.impactRadiusUrl;
        break;
      default:
        impactUrl = this.impactRadiusUrl;
    }

    return `${impactUrl}?${
      Object.keys(params).map((key) => `${key}=${params[key]}`).join('&')}`;
  }

  subId1() {
    if (this.mode === 'pagination') {
      return 'pagination';
    }

    const { classList } = document.body;

    if (classList.contains('home-home_vectors')) {
      return 'SearchPopHome';
    }

    if (isShowPage()) {
      return 'SearchPopShow';
    }

    if (isSearchPage()) {
      return 'SearchPopSearch';
    }

    return 'SearchPopOther';
  }

  subId2() {
    return document.body.getAttribute('data-pageview-guid');
  }

  subId3() {
    if (this.mode === 'pagination') return this.getNavigationPage();

    return `${this.searchTerm}_null_${this.mode}pop`;
  }

  get hasSearchValues() {
    return this.hasTermsValue && this.hasPageValue && this.hasSourceValue;
  }

  get destinationUrl() {
    return this.element.getAttribute('data-destination-url').replace(/{{search_term}}/g, this.searchTerm);
  }

  get adType() {
    return Cookies.get('content_type') || 'vector';
  }
}
